import { Layer, LayerProps } from 'shared/map-exports';
import { FeatureCollection } from 'geojson';
import { useCallback, useState } from 'react';
import { usePoisState } from 'state/usePoisState';
import { ENV } from 'shared/ENV';
import { addVisible } from 'shared/addVisible';
import {
  MapLayerProps,
  RadioTowerPoi,
  RadioTowerFeatureProperties,
  MapboxFeature,
  MapClickGetPois,
} from 'features/mapping/mapping.types';
import { safeParseDate } from 'shared/dates';
import { useMapLayerEvents } from 'features/mapping/hooks/useMapLayerEvents';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { RadioTowerIcon } from 'features/mapping/icons';
import { ArcGisLayer } from '../ArcGisLayer';

const MIN_ZOOM_VISIBILITY = 8;

export const RADIO_TOWER_ICON_LAYER_ID = 'radio-towers-symbol';

const radioTowerPropertiesToDialogProps = (
  properties: RadioTowerFeatureProperties,
): RadioTowerPoi => {
  return {
    editDate: safeParseDate(properties.EditDate),
    name: properties.name,
    channels: properties.channels.split('\n'),
  };
};

const postProcessData = (data: FeatureCollection): FeatureCollection => {
  // Transforms feature properties such that individual channel properties (channel1, channel2, ...)
  // are combined into a single string like "channel 1\nchannel 2\n" for display.
  const { features } = data;
  const processed = features.map((f) => {
    if (f.properties == null) return f;
    const channels = [];
    Object.entries(f.properties).forEach(([k, v]) => {
      if (k.startsWith('channel') && v != null) {
        channels.push(v);
      }
    });
    for (const k in Object.keys(f.properties)) {
      if (k.startsWith('channel')) {
        channels.push(f.properties[k]);
      }
    }
    return {
      ...f,
      properties: {
        ...f.properties,
        channels: channels.join('\n'),
      },
    };
  });

  return {
    ...data,
    features: processed,
  };
};

const radioTowersSymbolStyle = (
  hoveredId: number | null,
  selectedId: string | number | null | undefined,
): LayerProps => ({
  id: RADIO_TOWER_ICON_LAYER_ID,
  type: 'symbol',
  minzoom: MIN_ZOOM_VISIBILITY,
  layout: {
    'icon-image': [
      'case',
      [
        'in',
        ['get', 'OBJECTID'],
        ['literal', [hoveredId, selectedId].filter(Boolean)],
      ],
      'RadioTowerActive',
      'RadioTower',
    ],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-offset': RadioTowerIcon.offset,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ZOOM_VISIBILITY,
      0.8 * (RadioTowerIcon.scale ?? 1),
      20,
      1.5 * (RadioTowerIcon.scale ?? 1),
    ],
  },
});

export const radioTowerClickHandler: MapClickGetPois<
  RadioTowerFeatureProperties
> = (geoJsonFeatures) => {
  const { properties } = geoJsonFeatures[0];
  return [
    {
      type: 'radioTower',
      id: properties.OBJECTID,
      metadata: radioTowerPropertiesToDialogProps(properties),
    },
  ];
};

export const RadioTowersLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi } = usePoisState();
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const onHover = useCallback(
    (geoJsonFeatures: MapboxFeature<RadioTowerFeatureProperties>[]) => {
      const { properties } = geoJsonFeatures[0];
      setHoveredId(properties.OBJECTID);
    },
    [setHoveredId],
  );
  const onHoverOff = useCallback(() => {
    setHoveredId(null);
  }, [setHoveredId]);

  useMapLayerEvents<RadioTowerFeatureProperties>({
    layerId: RADIO_TOWER_ICON_LAYER_ID,
    enabled: visible,
    onHover,
    onHoverOff,
  });

  const selectedId =
    selectedPoi && selectedPoi.type === 'radioTower' ? selectedPoi.id : null;

  const radioTowersSrc = ENV.VITE_RADIO_TOWERS_URL;
  return (
    <ArcGisLayer
      visible={visible}
      src={radioTowersSrc}
      fields={[
        'EditDate',
        'name',
        'channel1',
        'channel2',
        'channel3',
        'channel4',
        'channel5',
        'channel6',
      ]}
      precision={4}
      staleTime={24 * 60 * 60 * 1000} // 24 hours
      postProcessDataFn={postProcessData}
    >
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(radioTowersSymbolStyle(hoveredId, selectedId), visible)}
      />
    </ArcGisLayer>
  );
};
